import type { BandSettings } from '../types'

export interface StockYearOption {
  year: number
  label: string
}

/**
 * The band years the stock order form offers, and how each one reads.
 *
 * The server decides which years are on offer (/bands/settings stockYears, from
 * stockBandYearsOffered() in api/v1/lib/stock-bands.php). Since Lauren's
 * 2026-09-03 request that is the next-season pre-order only, and the member
 * still has to pick it before paying.
 *
 * A year past the roster year is a pre-order and says so, whatever position it
 * holds in the list: the note used to hang off the array index, so the only
 * offered year would have read as plain "2027 bands" once the current year came
 * off the form.
 */
export function stockYearOptions(settings: Partial<Pick<BandSettings, 'stockYears' | 'rosterYear' | 'year'>>): StockYearOption[] {
  const years = settings.stockYears ?? []
  // settings.year is the personalized band year, which is rosterYear + 1.
  const rosterYear = settings.rosterYear ?? (settings.year ? settings.year - 1 : 0)

  return years
    .filter((year) => Number.isFinite(year))
    .map((year) => ({
      year,
      label: year > rosterYear ? `${year} bands (pre-order for next season)` : `${year} bands`,
    }))
}

/**
 * What the form offers when /bands/settings cannot be read.
 *
 * Never a guessed list: offering a year the server will reject sends the member
 * through the whole form to a 400, and guessing the current year would put back
 * the option the office asked us to remove. An empty list keeps the member on
 * the form with a message to reload.
 */
export function stockYearFallback(): number[] {
  return []
}
